import { useEffect, useRef, useState } from 'react';
import { readPlay3PlaceImage, readPlay3Portrait, readPlay3BeingPortrait } from '../cdo/cdoPlay3';
import { mediaSource } from '../shared/mediaSource';

const retryDelays = [2000, 5000, 15000, 30000, 60000];
const empty = playerId => ({ playerId, places: {}, beings: {}, portrait: {}, variants: {} });

// Saved artwork is independent of turns. Missing images and transient failures
// remain retryable; each completed read updates its card without waiting for peers.
export default function usePlay3Artwork(playerId, ready, placeIds, beingIds, portraitReferences = '[]') {
  const cache = useRef(empty(playerId));
  const [snapshot, setSnapshot] = useState(() => empty(playerId));
  useEffect(() => {
    if (cache.current.playerId !== playerId) cache.current = empty(playerId);
    const current = cache.current;
    const publish = () => setSnapshot({ playerId, places: { ...current.places },
      beings: { ...current.beings }, portrait: { ...current.portrait } });
    publish();
    if (!ready) return undefined;
    const controller = new AbortController();
    const references = new Map(JSON.parse(portraitReferences).map(([id, mediaId, version]) => [id, { mediaId, version }]));
    const targets = [
      { key: playerId, group: 'portrait', read: () => readPlay3Portrait(playerId, controller.signal) },
      ...placeIds.split('|').filter(Boolean).map(id => ({ key: id, group: 'places',
        read: () => readPlay3PlaceImage(playerId, id, controller.signal) })),
      ...beingIds.split('|').filter(id => id && id !== playerId).map(id => ({ key: id, group: 'beings',
        versionKey: `${id}:${references.get(id)?.mediaId || 'primary'}:${references.get(id)?.version || ''}`,
        read: () => readPlay3BeingPortrait(playerId, id, controller.signal, references.get(id)?.mediaId) })),
    ];
    const get = target => target.group === 'portrait' ? current.portrait : current[target.group][target.key] || {};
    const loaded = (target, state) => state.src && state.versionKey === (target.versionKey || target.key);
    const variantKey = target => `${target.group}:${target.versionKey || target.key}`;
    const put = (target, value) => {
      if (target.group === 'portrait') current.portrait = value;
      else current[target.group][target.key] = value;
    };
    for (const target of targets) {
      const saved = current.variants[variantKey(target)];
      if (saved) put(target, saved);
      const state = get(target);
      if (!loaded(target, state) && !state.failures) put(target, { ...state, isLoading: true });
    }
    publish();
    let timer;
    const requests = { active: 0 };
    const pending = new Set();
    const pump = () => {
      if (controller.signal.aborted) return;
      clearTimeout(timer);
      for (const target of targets) {
        if (requests.active >= 4) break;
        const state = get(target);
        if (loaded(target, state) || pending.has(target) || state.retryAt > Date.now()) continue;
        pending.add(target); requests.active++;
        put(target, { ...state, isLoading: true }); publish();
        Promise.resolve().then(target.read).then(result => {
          if (controller.signal.aborted) return;
          const src = mediaSource(result);
          if (src) {
            const value = { src, versionKey: target.versionKey || target.key };
            if (Object.keys(current.variants).length >= 512) delete current.variants[Object.keys(current.variants)[0]];
            current.variants[variantKey(target)] = value; put(target, value);
          }
          else retry(target, state);
          publish();
        }).catch(error => {
          if (!controller.signal.aborted) { retry(target, state, error.message); publish(); }
        }).finally(() => { pending.delete(target); requests.active--; pump(); });
      }
      if (requests.active < 4) {
        const due = targets.filter(target => !loaded(target, get(target)) && !pending.has(target))
          .map(target => get(target).retryAt || Date.now());
        if (due.length) timer = setTimeout(pump, Math.max(0, Math.min(...due) - Date.now()));
      }
    };
    const retry = (target, state, error) => {
      const failures = (state.failures || 0) + 1;
      put(target, { ...get(target), isLoading: false, error, failures,
        retryAt: Date.now() + retryDelays[Math.min(failures - 1, retryDelays.length - 1)] });
    };
    pump();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [playerId, ready, placeIds, beingIds, portraitReferences]);
  return snapshot.playerId === playerId ? snapshot : empty(playerId);
}
